/**
 * The specialty-module catalog — what a practice admin sees when browsing which specialty content
 * packs exist, not just a single derm on/off switch.
 *
 * Direct user instruction, 2026-08-18: dermatology (`BACKLOG.md` M3) is the first specialty module
 * actually built, but the product concept it belongs to is a *list* of specialty modules — most of
 * them not yet built at all. `BACKLOG.md`'s M5-M22 catalogues eighteen future candidates. This module
 * is the registry that makes that catalog a real, typed list rather than sixteen or so lines of UI
 * prose someone would have to keep in sync by hand, so the demo shows the actual product shape
 * (a growing catalog, one real entry today) instead of one feature flag standing alone.
 *
 * **This is a gating/UI-list change, not eighteen more modules.** Every `comingSoon` entry below
 * carries no schema, no capability, no clinical content, and no entitlement — `feature: null` is not
 * a placeholder to fill in later on this pass, it is the honest fact that there is nothing to entitle
 * yet. `dermatology`, `aesthetics-med-spa`, `physical-therapy-rehab`, `pediatrics`,
 * `gastroenterology`, and `orthopedics` are the six modules with a real `feature`
 * (`entitlements.ts`'s corresponding `*Module` entries), because they are the only ones with
 * anything behind them to gate — `BACKLOG.md` M9 shipped the second, 2026-08-18; M5/M6/M7/M8 shipped
 * the rest, 2026-08-24, as a deliberate sequencing override matching M3's own (this section's own
 * header above says "Research only... not authorization to begin any of it" for M5-M11 — M5-M8 are
 * the direct-instruction exception to that default, the same shape M3's row records; each
 * specialty-module row's own `BACKLOG.md` entry records this file as touching only its own array
 * entry, since sibling agents building other specialty modules in the same parallel batch edit this
 * file concurrently). M11's own `obgynModule` feature exists (`entitlements.ts`) and is real/tested,
 * but `ob-gyn` stays `comingSoon` here below — its backend shipped 2026-08-26 with no web UI yet, so
 * there is genuinely nothing to route a user to; see that array entry's own comment.
 */

import type { Feature } from './entitlements.ts';

export const SPECIALTY_MODULE_STATUSES = ['available', 'comingSoon'] as const;
export type SpecialtyModuleStatus = (typeof SPECIALTY_MODULE_STATUSES)[number];

export interface SpecialtyModuleDescriptor {
  readonly id: string;
  readonly label: string;
  readonly status: SpecialtyModuleStatus;
  /**
   * The commercial feature gating this module via `isEntitled`, or `null` for a `comingSoon` entry —
   * there is nothing built yet for a `comingSoon` module to gate. An `available` entry must always
   * carry a real `Feature`; `specialtyModules.test.ts` asserts this rather than trusting it.
   */
  readonly feature: Feature | null;
  /**
   * The path an entitled caller is sent to, or `null` for a `comingSoon` entry — there is nowhere
   * to send anyone yet. An `available` entry must always carry a real route.
   *
   * Found in review 2026-08-18: `SpecialtyModulesPage.tsx`'s first version hardcoded `/dermatology`
   * as the link target for *every* `available` row, because dermatology was the only one. That
   * works only by coincidence today — the moment a second module (e.g. `aesthetics-med-spa`, M9)
   * ships and flips to `available`, every available row, dermatology included, would still link to
   * `/dermatology`, silently routing a user into the wrong specialty's chart. The registry now
   * carries the route itself, so the UI derives the link rather than assuming there is only one.
   */
  readonly route: string | null;
}

/**
 * Ordered as `BACKLOG.md` M3, M5-M22 lists them. `dermatology`, `aesthetics-med-spa`,
 * `physical-therapy-rehab`, `pediatrics`, `gastroenterology`, and `orthopedics` are the `available`
 * entries; everything else is `comingSoon` until it is actually built, at which point it moves
 * status and gains a real `feature` and `route` the same way those six did — this registry, not a
 * new mechanism, is where that change lands.
 */
export const SPECIALTY_MODULES: readonly SpecialtyModuleDescriptor[] = [
  {
    id: 'dermatology',
    label: 'Dermatology',
    status: 'available',
    feature: 'dermatologyModule',
    route: '/dermatology',
  },
  {
    id: 'gastroenterology',
    label: 'Gastroenterology',
    status: 'available',
    feature: 'gastroenterologyModule',
    route: '/gastroenterology',
  },
  {
    id: 'physical-therapy-rehab',
    label: 'Physical therapy & rehab',
    status: 'available',
    feature: 'physicalTherapyModule',
    route: '/physical-therapy',
  },
  {
    id: 'orthopedics',
    label: 'Orthopedics',
    status: 'available',
    feature: 'orthopedicsModule',
    route: '/orthopedics',
  },
  {
    id: 'pediatrics',
    label: 'Pediatrics',
    status: 'available',
    feature: 'pediatricsModule',
    route: '/pediatrics',
  },
  {
    id: 'aesthetics-med-spa',
    label: 'Aesthetics / med spa',
    status: 'available',
    feature: 'aestheticsModule',
    route: '/aesthetics',
  },
  {
    id: 'ophthalmology',
    label: 'Ophthalmology',
    status: 'available',
    feature: 'ophthalmologyModule',
    route: '/ophthalmology',
  },
  {
    id: 'ob-gyn',
    label: 'OB/GYN',
    // Deliberately still comingSoon despite `obgynModule` being a real, built, tested entitlement
    // (BACKLOG.md M11) — the backend (schema/services/routes) is real, but no web UI exists yet
    // (no PatientObgynPage.tsx, no web/src/api/obgyn.ts, no chart tab), so flipping this to
    // `available` would list a real catalog entry with a route nothing renders at, the exact
    // "feature: null is not a placeholder, it's the honest fact there's nothing to entitle yet"
    // reasoning this file's own header states, applied to the UI side of "nothing to route to yet."
    status: 'comingSoon',
    feature: null,
    route: null,
  },
  { id: 'urology', label: 'Urology', status: 'comingSoon', feature: null, route: null },
  { id: 'chiropractic', label: 'Chiropractic', status: 'comingSoon', feature: null, route: null },
  { id: 'ent', label: 'ENT', status: 'comingSoon', feature: null, route: null },
  { id: 'rheumatology', label: 'Rheumatology', status: 'comingSoon', feature: null, route: null },
  { id: 'podiatry', label: 'Podiatry', status: 'comingSoon', feature: null, route: null },
  {
    id: 'pain-management',
    label: 'Pain management',
    status: 'comingSoon',
    feature: null,
    route: null,
  },
  { id: 'urgent-care', label: 'Urgent care', status: 'comingSoon', feature: null, route: null },
  {
    id: 'allergy-immunology',
    label: 'Allergy & immunology',
    status: 'comingSoon',
    feature: null,
    route: null,
  },
  { id: 'cardiology', label: 'Cardiology', status: 'comingSoon', feature: null, route: null },
  {
    id: 'oncology-hematology',
    label: 'Oncology / hematology',
    status: 'comingSoon',
    feature: null,
    route: null,
  },
  { id: 'nephrology', label: 'Nephrology', status: 'comingSoon', feature: null, route: null },
] as const;

export function specialtyModuleById(id: string): SpecialtyModuleDescriptor | undefined {
  return SPECIALTY_MODULES.find((module) => module.id === id);
}
